import type { IBrand, IExploreCountry, IExploreRegion, IProduct } from '@atlas/types'
import type { BrandPage, ProductPage } from '@/domain/explore.types'
import { apiClient } from './api.client'

/**
 * Explore-funnel + brand/product API client, all served by classification-service.
 */
const unwrap = <T>(data: { data: T }): T => data.data

/** What `POST /brands` accepts. `code` is the stable slug — immutable once created. */
export interface BrandCreate {
	code: string
	name: string
	description?: string
	logoRef?: string
	businessUnit?: string
	meta?: Record<string, unknown>
}

/** What `PATCH /brands/{id}` accepts. Every field optional; `code` is not among them. */
export type BrandUpdate = Partial<{
	name: string
	description: string | null
	logoRef: string | null
	businessUnit: string | null
	meta: Record<string, unknown>
	status: string
}>

export interface ProductCreate {
	code: string
	name: string
	brandId: string
	subtitle?: string
	therapeuticClass?: string
	principalClaim?: string
	durationSeal?: string
	imageRef?: string
	meta?: Record<string, unknown>
}

export type ProductUpdate = Partial<{
	name: string
	subtitle: string | null
	therapeuticClass: string | null
	principalClaim: string | null
	durationSeal: string | null
	imageRef: string | null
	meta: Record<string, unknown>
	status: string
}>

export const exploreApi = {
	regions: () => apiClient.get<{ data: IExploreRegion[] }>('/explore/regions').then((r) => unwrap(r.data)),
	countries: (regionCode: string) =>
		apiClient
			.get<{ data: IExploreCountry[] }>(`/explore/regions/${encodeURIComponent(regionCode)}/countries`)
			.then((r) => unwrap(r.data)),
	/**
	 * Distinct assets per brand (brandId → count) across a whole scope.
	 *
	 * Separate from `countries` because the funnel fans out one request per region, and a brand's
	 * true total spans them — a file tagged `global` is in every region's response, so no
	 * combination of those payloads yields it. One scope-wide read is the only way to get it.
	 */
	brandTotals: (regionCode?: string | null) =>
		apiClient
			.get<{ data: Record<string, number> }>('/explore/brand-totals', {
				params: regionCode ? { region: regionCode } : undefined,
			})
			.then((r) => unwrap(r.data)),
	/**
	 * The brand page, optionally scoped to a market. NOT unwrapped: this endpoint
	 * returns its composition at the top level, matching its `/products/{id}/page`
	 * sibling — only the list endpoints wrap in `{ data }`.
	 */
	brandPage: (brandId: string, market?: string | null) =>
		apiClient
			.get<BrandPage>(`/brands/${encodeURIComponent(brandId)}/page`, {
				params: market ? { market } : undefined,
			})
			.then((r) => r.data),
	/**
	 * The product page, resolved for one geography. Top-level like its brand sibling, hence
	 * not unwrapped.
	 *
	 * `market` carries a region OR a country: the endpoint validates the code, reports what
	 * applies at it and every level above it, and counts what the product's assets reach
	 * under it — none of which a client can derive. The page's other filters narrow its
	 * CONTENT rather than resolving it, so they go to the asset and rule reads instead.
	 */
	productPage: (productId: string, market?: string | null) =>
		apiClient
			.get<ProductPage>(`/products/${encodeURIComponent(productId)}/page`, {
				params: market ? { market } : undefined,
			})
			.then((r) => r.data),

	// ─── Catalog writes ──────────────────────────────────────────────────────────

	/**
	 * Create a brand. `code` is the stable slug and is immutable afterwards, because it is
	 * also the code of the axis value the brand is created alongside.
	 */
	createBrand: (body: BrandCreate) => apiClient.post<IBrand>('/brands', body).then((r) => r.data),
	updateBrand: (brandId: string, body: BrandUpdate) =>
		apiClient.patch<IBrand>(`/brands/${encodeURIComponent(brandId)}`, body).then((r) => r.data),
	/** Create a product under a brand. Same immutable-`code` rule as a brand. */
	createProduct: (body: ProductCreate) => apiClient.post<IProduct>('/products', body).then((r) => r.data),
	updateProduct: (productId: string, body: ProductUpdate) =>
		apiClient.patch<IProduct>(`/products/${encodeURIComponent(productId)}`, body).then((r) => r.data),
}
